import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { FiAward, FiCalendar, FiExternalLink } from "react-icons/fi"

import { getMyCertificates } from "../../../services/operations/assessmentAPI"
import CertificateCard from "../Certificate/CertificateCard"

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border border-richblack-700 bg-richblack-800 p-6">
      <div className="mb-4 h-4 w-40 rounded bg-richblack-700" />
      <div className="mb-2 h-6 w-64 rounded bg-richblack-700" />
      <div className="mb-4 h-3 w-32 rounded bg-richblack-700" />
      <div className="flex gap-2">
        {[1,2,3].map(i => <div key={i} className="h-5 w-16 rounded-full bg-richblack-700" />)}
      </div>
    </div>
  )
}

export default function MyCertificates() {
  const { token } = useSelector((s) => s.auth)
  const { user }  = useSelector((s) => s.profile)

  const [certs,   setCerts]   = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    ;(async () => {
      const data = await getMyCertificates(token)
      setCerts(data)
      setLoading(false)
    })()
  }, [token])

  const studentName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()

  return (
    <div className="mx-auto max-w-4xl px-4 pb-16">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-50/10">
          <FiAward size={20} className="text-yellow-50" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-richblack-5">My Certificates</h1>
          <p className="text-sm text-richblack-400">{certs.length} certificate{certs.length !== 1 ? "s" : ""} earned</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <SkeletonCard /><SkeletonCard />
        </div>
      ) : certs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-richblack-600 bg-richblack-800 py-20 text-center">
          <FiAward size={40} className="mb-4 text-richblack-500" />
          <p className="text-lg font-semibold text-richblack-5">No certificates yet</p>
          <p className="mt-1 text-sm text-richblack-400">
            Complete a course and pass the assessment to earn your first certificate.
          </p>
          <Link
            to="/dashboard/enrolled-courses"
            className="mt-5 rounded-lg bg-yellow-50 px-5 py-2 text-sm font-semibold text-richblack-900 hover:bg-yellow-100"
          >
            Go to My Courses
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {certs.map((cert) => (
            <div key={cert._id}>
              {/* Collapsed header row */}
              <button
                onClick={() => setSelected(selected === cert._id ? null : cert._id)}
                className="w-full rounded-2xl border border-richblack-700 bg-richblack-800 p-5 text-left transition-all hover:border-richblack-600"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {cert.course?.thumbnail ? (
                      <img
                        src={cert.course.thumbnail}
                        alt={cert.course.courseName}
                        className="h-12 w-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-16 items-center justify-center rounded-lg bg-richblack-700">
                        <FiAward size={20} className="text-yellow-50" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-richblack-5">{cert.course?.courseName ?? "Course"}</p>
                      <p className="flex items-center gap-1.5 text-xs text-richblack-400">
                        <FiCalendar size={11} />
                        {new Date(cert.issuedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-caribbeangreen-300/15 px-3 py-0.5 text-xs font-semibold text-caribbeangreen-300">
                      {cert.totalScore}%
                    </span>
                    <Link
                      to={`/certificate/${cert.credentialId}`}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      className="rounded-lg border border-richblack-600 p-1.5 text-richblack-400 transition-colors hover:text-richblack-100"
                      title="Public verify link"
                    >
                      <FiExternalLink size={14} />
                    </Link>
                  </div>
                </div>
              </button>

              {/* Expanded certificate card */}
              {selected === cert._id && (
                <div className="mt-3">
                  <CertificateCard certificate={cert} studentName={studentName} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
